import type { AtlasMap } from '@/models/interfaces/map/AtlasMap'
import type { Layer } from '@/models/interfaces/map/Layer'
import type { MapState } from '@/models/interfaces/map/MapState'
import type { MyMapStoreType } from '@/models/interfaces/Stores'
import type { LngLatBounds } from 'maplibre-gl'

export class MapStoreSerializationService {
  static serializeStore(store: MyMapStoreType): string {
    // Pinia composition stores auto-unwrap refs — store.mapDrawApi is already UseMapDrawReturn | null
    const drawApi = store.mapDrawApi
    const rawDrawFeatures = drawApi?.drawFeatures.value.features ?? []
    const completedDrawFeatures = rawDrawFeatures.filter(
      (f) => !(f.properties as Record<string, unknown> | null)?.currentlyDrawing,
    )
    const rawAnnotations = drawApi?.pointAnnotations.value ?? {}
    const mapInstance = store.map
    let bboxToSave: LngLatBounds = store.bbox as LngLatBounds
    if (mapInstance?.getBounds) {
      const bounds = mapInstance.getBounds()
      const sw = bounds.getSouthWest()
      const ne = bounds.getNorthEast()
      bboxToSave = { _sw: { lng: sw.lng, lat: sw.lat }, _ne: { lng: ne.lng, lat: ne.lat } } as LngLatBounds
    }
    const zoomToSave =
      typeof mapInstance?.getZoom === 'function' ? mapInstance.getZoom() : undefined
    const stateToSave: MapState = {
      layers: {},
      bbox: bboxToSave,
      order: [],
      zoom: zoomToSave,
      visibleFondStyleIds: [...store.visibleFondStyleIds],
      visibleOsmStyleIds: [...store.visibleOsmStyleIds],
      drawFeatures: completedDrawFeatures.length > 0 ? completedDrawFeatures : undefined,
      drawPointAnnotations:
        Object.keys(rawAnnotations).length > 0 ? { ...rawAnnotations } : undefined,
      userDroppedLayers:
        store.userDroppedLayers.length > 0
          ? store.userDroppedLayers.map((l) => ({
              name: l.name,
              geojson: l.geojson,
              isShown: l.isShown,
            }))
          : undefined,
    }
    // Do not export actor/project/resource layers so pasted links do not show these markers.
    if (store.atlasMaps.some((x: AtlasMap) => x.mainLayer.isShown)) {
      stateToSave.layers.atlasMaps = store.atlasMaps
        .filter((x: AtlasMap) => x.mainLayer.isShown)
        .map((x: AtlasMap) => ({
          id: x.id,
          subLayers: x.subLayers
            .filter((l: Layer) => l.isShown)
            .map((l: Layer) => l.id) as string[],
        }))
    }
    store.legendList.map((x) => stateToSave.order.push(x.id))

    return btoa(encodeURIComponent(JSON.stringify(stateToSave)))
  }

  static deserializeStore(encodedState: string): MapState {
    try {
      const decodedString = decodeURIComponent(atob(encodedState))
      const decodedState = JSON.parse(decodedString)
      return decodedState || {}
    } catch (error) {
      console.error('Error parsing store state:', error)
      return {} as MapState
    }
  }
}
